import React from 'react';
import { motion } from 'framer-motion';
import {
    BookOpenIcon,
    AcademicCapIcon,
    SparklesIcon,
    HeartIcon,
    UserGroupIcon,
    BeakerIcon,
    PuzzlePieceIcon
} from '@heroicons/react/24/outline';

const Curriculum = () => {
    const levels = [
        {
            title: "Pre-Nursery",
            description: "Focused on sensory exploration, early social skills and motor development through play based activities infused with Islamic manners and values.",
            icon: SparklesIcon,
            color: "from-pink-500 to-pink-700"
        },
        {
            title: "Nursery",
            description: "Early exposure to letters, numbers and thematic learning. Stories from the Quran, duas and adab (etiquette) are gently introduced through creative methods.",
            icon: BookOpenIcon,
            color: "from-purple-500 to-purple-700"
        },
        {
            title: "Kindergarten",
            description: "Strengthens phonics, numeracy, early writing and environmental awareness. Islamic learning continues through Seerah stories, nasheeds and themed class discussions.",
            icon: AcademicCapIcon,
            color: "from-blue-500 to-blue-700"
        },
        {
            title: "Grade 1 (Nazra begins)",
            description: "Core Cambridge subjects: English, Math, Science and Urdu. Students begin Nazra (Quran recitation) and continue daily Islamic studies, Arabic vocabulary and moral development.",
            icon: HeartIcon,
            color: "from-green-500 to-green-700"
        },
        {
            title: "Grade 2",
            description: "Deeper focus on critical thinking and communication across subjects. Quranic recitation progresses and students explore Islamic history, akhlaq (character) and core concepts of faith.",
            icon: UserGroupIcon,
            color: "from-yellow-500 to-orange-600"
        },
        {
            title: "Grade 3",
            description: "Fosters curiosity, solid understanding, and growing confidence. Students strengthen their thinking skills, improve Quran fluency, and deepen their understanding of Islamic teachings as they progress to higher levels.",
            icon: BookOpenIcon,
            color: "from-red-500 to-red-700"
        }
    ];

    const coCurricularActivities = [
        {
            title: "Islamic Tarbiyah Sessions",
            description: "Weekly values based discussions",
            icon: HeartIcon
        },
        {
            title: "STEM Foundation Activities",
            description: "Logic games and creative problem solving",
            icon: BeakerIcon
        },
        {
            title: "Outdoor Learning & Teamwork Games",
            description: "Physical activities and collaborative learning",
            icon: UserGroupIcon
        },
        {
            title: "Arts & Crafts with Moral Themes",
            description: "Creative expression with meaningful content",
            icon: PuzzlePieceIcon
        }
    ];

    return (
        <div className="min-h-screen bg-white">
            {/* Hero Section */}
            <section className="relative flex items-center justify-center min-h-screen overflow-hidden">
                <div
                    className="absolute inset-0 transform scale-110 bg-center bg-no-repeat bg-cover"
                    style={{
                        backgroundImage: `url('${require('../../assets/update/Our-Curriculum.jpeg')}')`
                    }}
                />
                <div className="absolute inset-0 bg-black/60" />

                <div className="relative z-10 max-w-4xl px-4 mx-auto text-center text-white">
                    <motion.h1
                        className="mb-6 text-5xl font-bold md:text-7xl"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                        OUR <span className="text-yellow-400">CURRICULUM</span>
                    </motion.h1>

                    <motion.p
                        className="mb-8 text-xl text-gray-200 md:text-2xl"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                    >
                        A Strong Start Rooted in Knowledge & Faith
                    </motion.p>
                </div>
            </section>

            {/* Introduction Section */}
            <section className="py-20 bg-white">
                <div className="container max-w-6xl px-4 mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="mb-8 text-center"
                    >
                        <p className="max-w-4xl mx-auto text-xl leading-relaxed text-gray-700">
                            At Aureus Cambridge School, we provide a nurturing space for children to grow intellectually,
                            emotionally and spiritually. Our programme blends the internationally recognised Cambridge
                            Curriculum with Islamic values and Tarbiyah, helping young learners develop a love for both
                            learning and Deen.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Levels Offered Section */}
            <section className="py-12 bg-gray-50">
                <div className="container max-w-6xl px-4 mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="mb-10 text-center"
                    >
                        <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">
                            Levels Offered <span className="text-yellow-500">(2025–26 Academic Year)</span>
                        </h2>
                    </motion.div>

                    <div className="max-w-6xl mx-auto">
                        {/* First row - 3 cards */}
                        <div className="grid grid-cols-1 gap-8 mb-8 md:grid-cols-3">
                            {levels.slice(0, 3).map((level, index) => {
                                const IconComponent = level.icon;
                                return (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 30 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.6, delay: index * 0.1 }}
                                        viewport={{ once: true }}
                                        className="p-8 transition-shadow duration-300 bg-white border border-gray-100 shadow-lg rounded-2xl hover:shadow-xl"
                                    >
                                        <div className="inline-flex items-center justify-center w-16 h-16 mb-6 bg-yellow-100 rounded-xl">
                                            <IconComponent className="w-8 h-8 text-yellow-600" />
                                        </div>
                                        <h3 className="mb-4 text-xl font-bold text-gray-900">{level.title}</h3>
                                        <p className="leading-relaxed text-gray-600">{level.description}</p>
                                    </motion.div>
                                );
                            })}
                        </div>

                        {/* Second row - 2 cards centered */}
                        <div className="flex justify-center gap-8">
                            {levels.slice(3, 6).map((level, index) => {
                                const IconComponent = level.icon;
                                return (
                                    <motion.div
                                        key={index + 3}
                                        initial={{ opacity: 0, y: 30 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.6, delay: (index + 3) * 0.1 }}
                                        viewport={{ once: true }}
                                        className="w-full max-w-sm p-8 transition-shadow duration-300 bg-white border border-gray-100 shadow-lg rounded-2xl hover:shadow-xl"
                                    >
                                        <div className="inline-flex items-center justify-center w-16 h-16 mb-6 bg-yellow-100 rounded-xl">
                                            <IconComponent className="w-8 h-8 text-yellow-600" />
                                        </div>
                                        <h3 className="mb-4 text-xl font-bold text-gray-900">{level.title}</h3>
                                        <p className="leading-relaxed text-gray-600">{level.description}</p>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </section>

            {/* Co-Curricular Enrichment Section */}
            <section className="py-20 bg-white">
                <div className="container max-w-6xl px-4 mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="mb-16 text-center"
                    >
                        <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">
                            Co-Curricular <span className="text-yellow-500">Enrichment</span>
                        </h2>
                        <p className="text-xl text-gray-600">
                            (All Levels)
                        </p>
                    </motion.div>

                    <div className="grid gap-8 md:grid-cols-2">
                        {coCurricularActivities.map((activity, index) => {
                            const IconComponent = activity.icon;
                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    className="flex items-start gap-4 p-6 transition-colors duration-300 bg-gray-50 rounded-xl hover:bg-gray-100"
                                >
                                    <div className="flex-shrink-0">
                                        <div className="inline-flex items-center justify-center w-12 h-12 bg-yellow-100 rounded-lg">
                                            <IconComponent className="w-6 h-6 text-yellow-600" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="mb-2 text-lg font-bold text-gray-900">{activity.title}</h3>
                                        <p className="text-gray-600">{activity.description}</p>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Call to Action Section */}
            <section className="py-20 bg-gray-100">
                <div className="container max-w-4xl px-4 mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="mb-8 text-3xl font-bold text-gray-900 md:text-4xl">
                            Ready to Begin Your Child's Journey?
                        </h2>
                        <p className="max-w-2xl mx-auto mb-8 text-xl text-gray-600">
                            Discover how our unique blend of Cambridge curriculum and Islamic values can nurture your child's potential.
                        </p>
                        <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
                            <a
                                href="/apply-online"
                                className="inline-flex items-center justify-center w-full px-8 py-4 text-lg font-semibold text-black transition-colors duration-300 bg-yellow-400 rounded-full sm:w-auto hover:bg-yellow-500"
                            >
                                Apply for Admission
                            </a>
                            <a
                                href="/contact"
                                className="inline-flex items-center justify-center w-full px-8 py-4 text-lg font-semibold text-gray-900 transition-colors duration-300 border-2 border-gray-900 rounded-full sm:w-auto hover:bg-gray-900 hover:text-white"
                            >
                                Learn More
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default Curriculum;
